import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import type { Id, InboundStatus, NotificationEvent, OutboundEmailStatus } from '@/api/types'
import { requireAdmin } from '@/auth/guards'
import { FilterBar, FilterField } from '@/components/filter-bar'
import { Frame } from '@/components/frame'
import { EmailIdentityPanel } from '@/components/notifications/email-identity-panel'
import { InboundTable } from '@/components/notifications/inbound-table'
import { MailStatusCard } from '@/components/notifications/mail-status-card'
import { OutboxDetailDialog } from '@/components/notifications/outbox-detail-dialog'
import { OutboxTable } from '@/components/notifications/outbox-table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  INBOUND_STATUS_LABELS,
  NOTIFICATION_EVENT_LABELS,
  OUTBOUND_EMAIL_STATUS_LABELS,
} from '@/lib/labels'
import {
  INBOUND_STATUS_ORDER,
  NOTIFICATION_EVENT_ORDER,
  OUTBOUND_EMAIL_STATUS_ORDER,
} from '@/lib/orders'
import { integer, oneOf, orElse, readSearch, text } from '@/lib/search-params'

const STATUSES = OUTBOUND_EMAIL_STATUS_ORDER
const INBOUND_STATUSES = INBOUND_STATUS_ORDER
const EVENTS = NOTIFICATION_EVENT_ORDER
const TOUS = 'tous'

/** Filtres portés par l'URL : une vue se partage et survit à un rechargement. */
const SHAPE = {
  /** L'onglet « Identité » (1.39.0) ; absent : le suivi des envois. */
  onglet: oneOf(['identite', 'reponses'] as const),
  /** #335 tranche 3 : les réponses reçues, par état. */
  reponse: oneOf(INBOUND_STATUSES),
  statut: oneOf(STATUSES),
  evenement: oneOf(EVENTS),
  destinataire: text(),
  page: orElse(integer({ min: 1 }), 1),
}

/**
 * Réglages › Courriels, **admin** : le suivi des envois, avec son détail, la
 * relance d'un échec, l'annulation d'un message programmé, l'état du
 * fournisseur et le message d'essai. Réservé parce que les adresses sont
 * personnelles.
 */
export const Route = createFileRoute('/_app/settings/emails')({
  beforeLoad: requireAdmin,
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: SettingsEmails,
})

function SettingsEmails() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [opened, setOpened] = useState<Id | null>(null)
  const [recipient, setRecipient] = useState(search.destinataire ?? '')

  const set = (patch: Partial<typeof search>) => {
    void navigate({ search: (current) => ({ ...current, page: 1, ...patch }) })
  }
  // Le serveur exige trois caractères au moins : on ne lui envoie pas moins.
  const recipientTooShort = recipient.trim() !== '' && recipient.trim().length < 3

  return (
    <Tabs
      value={search.onglet ?? 'suivi'}
      onValueChange={(value) => {
        void navigate({
          search: (current) => ({
            ...current,
            onglet: value === 'identite' || value === 'reponses' ? value : undefined,
            page: 1,
          }),
        })
      }}
      className="space-y-4"
    >
      <TabsList>
        <TabsTrigger value="suivi">Suivi des envois</TabsTrigger>
        <TabsTrigger value="reponses" data-testid="onglet-reponses">
          Réponses reçues
        </TabsTrigger>
        <TabsTrigger value="identite" data-testid="onglet-identite">
          Identité
        </TabsTrigger>
      </TabsList>
      <TabsContent value="reponses">
        <Frame className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="filtre-reponse">État</Label>
              <Select
                value={search.reponse ?? TOUS}
                onValueChange={(value) => {
                  set({ reponse: value === TOUS ? undefined : (value as InboundStatus) })
                }}
              >
                <SelectTrigger id="filtre-reponse" className="w-48" data-testid="filtre-reponse">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TOUS}>Toutes les réponses</SelectItem>
                  {INBOUND_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {INBOUND_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              Les réponses des clients et de l’équipe aux notifications, publiées au bon endroit ou
              refusées avec leur motif.
            </p>
          </div>
          <InboundTable
            params={{
              page: search.page,
              page_size: 25,
              ...(search.reponse ? { status: search.reponse } : {}),
            }}
            onPageChange={(page) => {
              void navigate({ search: (current) => ({ ...current, page }) })
            }}
          />
        </Frame>
      </TabsContent>
      <TabsContent value="identite">
        <Frame>
          <EmailIdentityPanel />
        </Frame>
      </TabsContent>
      <TabsContent value="suivi">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <Frame>
            <FilterBar
              search={
                <div className="space-y-1">
                  <Label htmlFor="filtre-destinataire" className="sr-only">
                    Destinataire
                  </Label>
                  <Input
                    id="filtre-destinataire"
                    value={recipient}
                    placeholder="Destinataire, trois caractères au moins"
                    aria-invalid={recipientTooShort}
                    className="w-full max-w-xs"
                    onChange={(event) => {
                      const next = event.target.value
                      setRecipient(next)
                      const trimmed = next.trim()
                      if (trimmed === '') set({ destinataire: undefined })
                      else if (trimmed.length >= 3) set({ destinataire: trimmed })
                    }}
                  />
                </div>
              }
              active={[
                ...(search.statut
                  ? [
                      {
                        key: 'statut',
                        label: OUTBOUND_EMAIL_STATUS_LABELS[search.statut],
                        onClear: () => {
                          set({ statut: undefined })
                        },
                      },
                    ]
                  : []),
                ...(search.evenement
                  ? [
                      {
                        key: 'evenement',
                        label: NOTIFICATION_EVENT_LABELS[search.evenement],
                        onClear: () => {
                          set({ evenement: undefined })
                        },
                      },
                    ]
                  : []),
              ]}
              panelTitle="Filtrer les courriels"
            >
              <FilterField label="État" htmlFor="filtre-statut">
                <Select
                  value={search.statut ?? TOUS}
                  onValueChange={(value) => {
                    set({ statut: value === TOUS ? undefined : (value as OutboundEmailStatus) })
                  }}
                >
                  <SelectTrigger id="filtre-statut" aria-label="Filtrer par état">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TOUS}>Tous les états</SelectItem>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {OUTBOUND_EMAIL_STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Évènement" htmlFor="filtre-evenement">
                <Select
                  value={search.evenement ?? TOUS}
                  onValueChange={(value) => {
                    set({ evenement: value === TOUS ? undefined : (value as NotificationEvent) })
                  }}
                >
                  <SelectTrigger id="filtre-evenement" aria-label="Filtrer par évènement">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TOUS}>Tous les évènements</SelectItem>
                    {EVENTS.map((event) => (
                      <SelectItem key={event} value={event}>
                        {NOTIFICATION_EVENT_LABELS[event]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </FilterBar>

            <OutboxTable
              params={{
                page: search.page,
                page_size: 25,
                ...(search.statut ? { status: search.statut } : {}),
                ...(search.evenement ? { event: search.evenement } : {}),
                ...(search.destinataire ? { recipient: search.destinataire } : {}),
              }}
              onPageChange={(page) => {
                void navigate({ search: (current) => ({ ...current, page }) })
              }}
              onOpen={setOpened}
            />
          </Frame>

          <MailStatusCard />

          <OutboxDetailDialog
            id={opened}
            onClose={() => {
              setOpened(null)
            }}
          />
        </div>
      </TabsContent>
    </Tabs>
  )
}
